import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LandingNav } from '../components/Nav';
import { COPY } from '../data';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { FooterNavLinks } from '../components/FooterNavLinks';
import LogoWithBeta from '../components/LogoWithBeta';
import { FERTILITY_DEMO_HOME } from '../constants/paths';

const GRAD = 'linear-gradient(140deg, rgb(251,234,240), rgb(225,245,238))';
const PK = 'rgb(212, 83, 126)';
const PKD = 'rgb(153, 53, 86)';
const CARD = 'rgb(255, 253, 249)';
const PAGE = 'rgb(249, 246, 241)';
const SAND = 'rgb(232, 224, 216)';
const MINT = 'rgb(225, 245, 238)';
const PURPLE = 'rgb(238, 237, 254)';
const AMBER = 'rgb(255, 241, 221)';
const AMBERD = 'rgb(146, 84, 10)';
const TXT = 'rgb(17, 17, 17)';
const MUTED = 'rgb(122, 107, 99)';

/** Matches `Landing.js` hero pill (`s.eyebrow`) — used on gradient bands */
const landingEyebrowPill = (compact) => ({
  fontSize: compact ? 10 : 11,
  fontWeight: 700,
  letterSpacing: '.12em',
  textTransform: 'uppercase',
  color: 'var(--pink)',
  background: 'var(--pink-l)',
  padding: compact ? '5px 12px' : '5px 16px',
  borderRadius: 999,
  marginBottom: 20,
  marginTop: 0,
  display: 'inline-block',
  textAlign: 'center',
  maxWidth: '100%',
});

/** Matches `FeaturesSection.jsx` section label (“What’s included”) */
const sectionEyebrow = ({ align = 'center' } = {}) => ({
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.1em',
  color: 'var(--muted)',
  textTransform: 'uppercase',
  textAlign: align,
  marginBottom: 12,
  marginTop: 0,
});

const emPink = { color: PK, fontStyle: 'italic' };

/** Homepage hero CTAs — mirror `Landing.js` `s.mainBtn`, `s.secBtn`, `s.heroBtns` */
const landingHeroCta = {
  wrap: {
    display: 'flex',
    gap: 12,
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginBottom: 0,
    position: 'relative',
    zIndex: 1,
  },
  mainBtn: {
    fontSize: 15,
    fontWeight: 700,
    color: 'white',
    background: 'var(--pink)',
    border: 'none',
    cursor: 'pointer',
    padding: '14px 32px',
    borderRadius: 999,
    boxShadow: 'none',
  },
  secBtn: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--pink-d)',
    background: 'var(--pink-l)',
    border: '1.5px solid var(--pink-m)',
    cursor: 'pointer',
    padding: '13px 26px',
    borderRadius: 999,
  },
};

const beliefCards = [
  {
    border: 'rgb(251,234,240)',
    icon: '🧠',
    title:
      "Every woman's journey is continuous — not a series of isolated events",
    body: "A miscarriage doesn't reset. A failed cycle isn't a blank slate. The platform carries your full history forward so the support you get reflects everything you've been through — not just where you are today.",
  },
  {
    border: MINT,
    icon: '🌸',
    title: 'The hardest moments happen between appointments',
    body: "Clinics see you for 20 minutes. The other 23 hours and 40 minutes — the 2 a.m. spiral, the retrieval morning, the day after a loss — that's where OrivonAI lives.",
  },
  {
    border: PURPLE,
    icon: '🔒',
    title: 'Your data belongs to you — not to advertisers, insurers, or anyone else',
    body: "Privacy isn't a feature we added. It's the reason the product exists this way. No ads. No data selling. Not now, not at scale. Not ever.",
  },
  {
    border: AMBER,
    icon: '🤝',
    title: 'The hard roads deserve the same care as the easy ones',
    body: "IVF, recurrent loss, surrogacy, infertility, postpartum — these aren't edge cases. They're the reason we exist. OrivonAI is built for the roads most apps look away from.",
  },
];

const FONT_BODY_ABOUT = '"Plus Jakarta Sans", -apple-system, sans-serif';

const whoCompanionChapters = [
  {
    img: '/images/chapter-heroes/fertility-homepage.png',
    alt: 'Woman smiling outdoors in natural light',
    emoji: '🌸',
    chapterName: 'Fertility',
    desc: 'Protocol tracking, lab decoding, medication reminders — the full treatment chapter',
    badge: null,
  },
  {
    img: '/images/chapter-heroes/ttc-homepage.png',
    alt: 'Couple celebrating a positive pregnancy test',
    emoji: '💜',
    chapterName: 'Trying to Conceive',
    desc: 'Cycle tracking, ovulation, and TTC support — before treatment ever begins',
    badge: null,
  },
  {
    img: '/images/chapter-heroes/pregnancy-homepage.png',
    alt: 'Happy couple cradling pregnant belly',
    emoji: '🤰',
    chapterName: 'Pregnancy',
    desc: 'Arrives knowing how hard the road was — support built for the path you took',
    badge: null,
  },
  {
    img: '/images/chapter-heroes/motherhood-homepage.png',
    alt: 'Mother and baby',
    emoji: '🤱',
    chapterName: 'Motherhood',
    desc: 'The fourth trimester and early years — for every way a woman becomes a mother',
    badge: 'Includes postpartum',
  },
];

export default function About() {
  const navigate = useNavigate();
  const m900 = useMediaQuery('(max-width: 900px)');
  const m680 = useMediaQuery('(max-width: 680px)');

  const sectionPad = m680 ? '48px 20px' : '72px 48px';
  const heroPad = m680 ? '48px 20px' : '64px 48px 72px';
  const h2Section = m680 ? 28 : 36;
  const h2Mission = m680 ? 28 : 32;

  return (
    <div style={{ minHeight: '100vh', background: PAGE }}>
      <div style={{ background: 'var(--white)', color: 'var(--text)' }}>
        <LandingNav />
      </div>

      <header
        style={{
          background: GRAD,
          padding: heroPad,
          textAlign: 'center',
        }}
      >
        <div
          style={{
            maxWidth: 900,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <div style={landingEyebrowPill(m680)}>About us</div>
          <h1
            className="serif"
            style={{
              fontSize: m680 ? 28 : 44,
              fontWeight: 700,
              color: TXT,
              lineHeight: 1.15,
              maxWidth: 720,
              margin: '0 0 20px',
            }}
          >
            Your reproductive journey, <em style={emPink}>supported at every stage.</em>
          </h1>
          <p
            style={{
              fontSize: 16,
              color: MUTED,
              maxWidth: 600,
              lineHeight: 1.75,
              margin: '0 0 20px',
            }}
          >
            OrivonAI is a health platform that supports every stage of the reproductive journey. By keeping health history,
            insights, and care connected, we provide more personalized support, reduce fragmented care, and help women make informed
            decisions during some of life&apos;s most challenging moments. We are not a clinic, but a trusted companion throughout the
            journey.
          </p>
          <div
            style={{
              ...landingHeroCta.wrap,
              marginTop: m680 ? 14 : 20,
              marginBottom: m680 ? 8 : 12,
              flexDirection: m680 ? 'column' : 'row',
              width: m680 ? '100%' : 'auto',
              maxWidth: m680 ? 360 : 'none',
              padding: m680 ? '0 8px' : 0,
              alignSelf: m680 ? 'stretch' : 'center',
            }}
          >
            <button
              type="button"
              style={{ ...landingHeroCta.mainBtn, width: m680 ? '100%' : 'auto', boxSizing: 'border-box' }}
              onClick={() => navigate('/signup?source=about')}
            >
              Join the waitlist
            </button>
            <Link
              to={FERTILITY_DEMO_HOME}
              style={{
                ...landingHeroCta.secBtn,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: m680 ? '100%' : 'auto',
                boxSizing: 'border-box',
              }}
            >
              See how it works
            </Link>
          </div>
        </div>
      </header>

      <main style={{ margin: 0, padding: 0, width: '100%', boxSizing: 'border-box' }}>
        {/* Section 2 — What We Believe */}
        <div
          style={{
            background: PAGE,
            padding: sectionPad,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 48,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <p style={sectionEyebrow()}>What we believe</p>
            <h2
              className="serif"
              style={{
                fontSize: h2Section,
                fontWeight: 700,
                color: TXT,
                lineHeight: 1.2,
                margin: 0,
                maxWidth: 640,
              }}
            >
              Women deserve better
              <br />
              <em style={emPink}>continuity of care.</em>
            </h2>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: m680 ? '1fr' : 'repeat(2, 1fr)',
              gap: 16,
              maxWidth: 900,
              width: '100%',
            }}
          >
            {beliefCards.map((c) => (
              <div
                key={c.title}
                style={{
                  background: CARD,
                  borderRadius: 14,
                  padding: m680 ? 22 : 26,
                  border: `0.5px solid ${SAND}`,
                  borderLeft: `3px solid ${c.border}`,
                  boxSizing: 'border-box',
                }}
              >
                <div style={{ fontSize: 22, marginBottom: 10 }}>{c.icon}</div>
                <p style={{ fontSize: 15, fontWeight: 700, color: TXT, lineHeight: 1.35, margin: '0 0 10px' }}>{c.title}</p>
                <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.65, margin: 0 }}>{c.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Section 3 — Who it's for */}
        <div
          style={{
            background: CARD,
            padding: sectionPad,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 40,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <p style={sectionEyebrow()}>Who it&apos;s for</p>
            <h2
              className="serif"
              style={{
                fontSize: m680 ? 28 : 34,
                fontWeight: 700,
                color: TXT,
                lineHeight: 1.2,
                margin: 0,
                maxWidth: 640,
              }}
            >
              One companion for <em style={emPink}>every stage</em>
            </h2>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: m680 ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))',
              gap: m680 ? 24 : 28,
              maxWidth: 900,
              width: '100%',
              margin: '0 auto',
              boxSizing: 'border-box',
            }}
          >
            {whoCompanionChapters.map((t) => (
              <div
                key={t.chapterName}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  gap: 12,
                  minWidth: 0,
                }}
              >
                <img
                  src={t.img}
                  alt={t.alt}
                  style={{
                    width: 112,
                    height: 112,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    objectPosition: 'center top',
                    border: '3px solid rgba(212, 83, 126, 0.35)',
                    outline: '3px solid rgba(212, 83, 126, 0.1)',
                    outlineOffset: 4,
                    flexShrink: 0,
                    display: 'block',
                    boxSizing: 'border-box',
                  }}
                />
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <span
                    style={{
                      display: 'block',
                      fontSize: 20,
                      lineHeight: 1,
                    }}
                    aria-hidden
                  >
                    {t.emoji}
                  </span>
                  <span
                    style={{
                      fontFamily: FONT_BODY_ABOUT,
                      fontSize: 14,
                      fontWeight: 700,
                      color: TXT,
                      letterSpacing: 'normal',
                      lineHeight: 1.4,
                      display: 'block',
                    }}
                  >
                    {t.chapterName}
                  </span>
                </div>
                <p
                  style={{
                    fontFamily: FONT_BODY_ABOUT,
                    fontSize: 12,
                    fontWeight: 400,
                    color: MUTED,
                    lineHeight: 1.6,
                    textAlign: 'center',
                    maxWidth: 180,
                    margin: 0,
                  }}
                >
                  {t.desc}
                </p>
                {t.badge ? (
                  <span
                    style={{
                      marginTop: 4,
                      background: AMBER,
                      color: AMBERD,
                      borderRadius: 999,
                      padding: '2px 8px',
                      fontSize: 9,
                      fontWeight: 700,
                      fontFamily: FONT_BODY_ABOUT,
                      display: 'inline-flex',
                      alignSelf: 'center',
                    }}
                  >
                    {t.badge}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        </div>

        {/* Section 3 — Mission */}
        <div
          style={{
            background: 'rgb(249, 246, 241)',
            backgroundImage: 'none',
            padding: sectionPad,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <p
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: PKD,
              letterSpacing: '1.6px',
              textTransform: 'uppercase',
              margin: '0 0 12px',
            }}
          >
            OUR MISSION
          </p>
          <h2
            className="serif"
            style={{
              fontSize: h2Mission,
              fontWeight: 700,
              color: TXT,
              lineHeight: 1.3,
              maxWidth: 660,
              margin: '0 0 20px',
            }}
          >
            A platform that meets every woman
            <br />
            <em className="serif" style={emPink}>
              exactly where she is.
            </em>
          </h2>
          <p
            style={{
              fontSize: 15,
              color: MUTED,
              maxWidth: 580,
              lineHeight: 1.75,
              margin: '0 0 8px',
            }}
          >
            Not where a generic checklist says she should be. Not starting over every time she moves forward. OrivonAI is built to
            hold the full story — the losses, the labs, the loneliness, and the hope — and stay with her through every chapter that
            follows.
          </p>
          <div
            style={{
              marginTop: 8,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              padding: m680 ? '0 8px' : 0,
              boxSizing: 'border-box',
            }}
          >
            <button
              type="button"
              style={{
                ...landingHeroCta.mainBtn,
                width: m680 ? '100%' : 'auto',
                maxWidth: m680 ? 360 : 'none',
                boxSizing: 'border-box',
              }}
              onClick={() => navigate('/signup?source=about')}
            >
              Join the waitlist
            </button>
          </div>
        </div>

        {/* Section 4 — The Founder */}
        <div
          style={{
            background: GRAD,
            padding: m680 ? '40px 20px' : '40px 48px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 32,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <p style={sectionEyebrow()}>The founder</p>
            <h2
              className="serif"
              style={{
                fontSize: h2Section,
                fontWeight: 700,
                color: TXT,
                lineHeight: 1.2,
                margin: 0,
              }}
            >
              Built by someone <em style={emPink}>who lived it.</em>
            </h2>
          </div>

          <div
            style={{
              maxWidth: 820,
              width: '100%',
              background: CARD,
              border: `0.5px solid ${SAND}`,
              borderRadius: 20,
              boxShadow: '0 2px 20px rgba(153, 53, 86, 0.08)',
              overflow: 'hidden',
              display: 'grid',
              gridTemplateColumns: m680 ? '1fr' : '260px 1fr',
            }}
          >
            {/* Wrapper stretches with the row; absolutely positioned img fills it (avoids letterboxing / gap under shorter <img>). */}
            <div
              style={{
                position: 'relative',
                width: '100%',
                overflow: 'hidden',
                borderRadius: m680 ? '20px 20px 0 0' : '20px 0 0 20px',
                ...(m680
                  ? { minHeight: 220, height: 220 }
                  : { minHeight: 300, alignSelf: 'stretch' }),
              }}
            >
              <img
                src="/images/blog/founder-amelie.png"
                alt="Vanessa Mosbacker, founder of OrivonAI"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center top',
                  display: 'block',
                }}
              />
            </div>
            <div
              style={{
                padding: m680 ? '24px 20px' : '36px',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                alignItems: 'flex-start',
                boxSizing: 'border-box',
              }}
            >
              <p style={sectionEyebrow({ align: 'left' })}>Founder &amp; CEO</p>
              <p className="serif" style={{ fontSize: 24, fontWeight: 700, color: TXT, margin: 0 }}>
                Vanessa Mosbacker
              </p>
              <p style={{ fontSize: 12, color: MUTED, margin: 0 }}>Serial founder · operator · investor</p>
              <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.7, margin: 0 }}>
                Seven years. Six losses. Four rounds of IVF. One surrogate. Two children. OrivonAI exists because Vanessa lived
                the gap between what the healthcare system offers and what women actually need — and built the platform she wished
                had existed.
              </p>
              <Link
                to="/blog/why-i-created-orivonai"
                style={{
                  marginTop: 8,
                  background: 'rgb(251, 234, 240)',
                  color: PKD,
                  borderRadius: 999,
                  padding: '8px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignSelf: 'flex-start',
                }}
              >
                Read her full story →
              </Link>
            </div>
          </div>
        </div>
      </main>

      <footer
        style={{
          padding: '28px 24px',
          borderTop: '1px solid var(--border)',
          background: 'var(--white)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 16,
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 11,
          color: 'var(--muted)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
            <LogoWithBeta compact={m900} />
          </Link>
          <span>© 2026 OrivonAI · {COPY.footerTagline}</span>
        </div>

        <FooterNavLinks />
      </footer>
    </div>
  );
}
